import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Package, Calendar, Download, Trash2, Clock, Search,
  Loader2, History, ArrowLeft, AlertTriangle, Filter,
  ChevronLeft, ChevronRight, ShoppingBag,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to ensure download URLs are absolute (prepends backend origin if relative)
const resolveDownloadUrl = (url) => {
  if (!url) return null;
  return url.startsWith("http")
    ? url
    : `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export default function OrderHistoryPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 10;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { state: { from: { pathname: "/orders" } } });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/user/purchases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const hideOrder = async (ref) => {
    try {
      await axios.post(`${API}/user/orders/hide`, { transaction_ref: ref }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(prev => prev.filter(o => o.transaction_ref !== ref));
      toast.success("Order removed from history");
    } catch {
      toast.error("Failed to remove order");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear entire order history? Download links will still work.")) return;
    try {
      await axios.delete(`${API}/user/orders/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders([]);
      toast.success("Order history cleared");
    } catch {
      toast.error("Failed to clear history");
    }
  };

  const formatPrice = (p) => new Intl.NumberFormat("en-GH", { style: "currency", currency: "GHS" }).format(p);
  const formatDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  // Filter + paginate
  const filtered = orders.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      o.transaction_ref?.toLowerCase().includes(q) ||
      o.items?.some(i => i.title?.toLowerCase().includes(q)) ||
      o.customer_email?.toLowerCase().includes(q)
    );
  });
  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="order-history-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild className="rounded-lg" data-testid="back-to-dashboard">
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
                <History className="w-6 h-6" /> Order History
              </h1>
              <p className="text-muted-foreground text-sm mt-0.5">
                {filtered.length} order{filtered.length !== 1 ? "s" : ""} found
              </p>
            </div>
          </div>
          {orders.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 rounded-lg"
              data-testid="clear-all-orders-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Clear All
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by product name or reference..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="pl-10 rounded-xl"
            data-testid="order-search"
          />
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : paginated.length > 0 ? (
          <div className="space-y-3">
            {paginated.map(order => (
              <Card key={order.transaction_ref} className="overflow-hidden" data-testid={`order-card-${order.transaction_ref}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <Package className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {order.items?.map((item, i) => (
                            <span key={i} className="font-medium text-sm">{item.title}</span>
                          ))}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {formatDate(order.created_at)}
                          </span>
                          <span>Ref: {order.transaction_ref}</span>
                          <span>{order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? "s" : ""}</span>
                          <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700 border-0">
                            Completed
                          </Badge>
                        </div>
                        <p className="text-primary font-bold mt-2">{formatPrice(order.total_amount)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {order.items?.map((item, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          className="rounded-lg text-xs"
                          data-testid={`download-${order.transaction_ref}-${i}`}
                          onClick={async () => {
                            try {
                              const res = await axios.get(
                                `${API}/download/${order.transaction_ref}?product_id=${item.product_id}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              if (res.data.download_url) {
                                window.open(resolveDownloadUrl(res.data.download_url), "_blank");
                              } else if (res.data.mock) {
                                toast.info("Mock download - PDF files not configured");
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Download failed");
                            }
                          }}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" /> {item.title?.substring(0, 15)}...
                        </Button>
                      ))}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => hideOrder(order.transaction_ref)}
                        className="text-muted-foreground hover:text-red-600 h-8 w-8 p-0 rounded-lg"
                        title="Remove from history"
                        data-testid={`hide-order-${order.transaction_ref}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4">
                <Button
                  variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)} className="rounded-lg"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground px-3">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)} className="rounded-lg"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ) : orders.length > 0 && filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
              <h3 className="font-heading text-lg font-bold mb-1">No matching orders</h3>
              <p className="text-muted-foreground text-sm">Try a different search term</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <h3 className="font-heading text-xl font-bold mb-2">No order history</h3>
              <p className="text-muted-foreground text-sm mb-6">
                You haven't made any purchases yet.
              </p>
              <Button asChild className="bg-primary rounded-xl">
                <Link to="/mock-exams">Browse Mock Exams</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {orders.length > 0 && (
          <div className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>Clearing history only hides orders from this view. Your download links and purchase records remain active.</span>
          </div>
        )}
      </div>
    </div>
  );
}
